import React, { useEffect, useState, useRef } from "react";
import { Box, Typography, Button } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import axios from "axios";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { useParams } from "react-router-dom";
import { Payment } from "../../../../models/payment";
import { Loan } from "../../../../models/loan";
import { useT } from "../../../../i18n";
import { formatCurrency as companyMoney } from '../../../../utils/format/currency';

interface InvoiceProps {
  isOpen: boolean;
  onClose: () => void;
  clientName: string;
  showAlert: (message: string, severity?: "info" | "error" | "success" | "warning") => void;
  showLoading: () => void;
  hideLoading: () => void;
  /** Abono a mostrar. */
  paymentId?: number;
  /** Préstamo de la ruta: se muestra su último abono (la lista de rutas tiene el préstamo, no el abono). */
  loanId?: number;
}

const ReceiptModal: React.FC<InvoiceProps> = ({ isOpen, onClose, clientName, showAlert, showLoading, hideLoading, paymentId, loanId }) => {
  const t = useT();
  const { id } = useParams();
  const [payment, setPayment] = useState<Payment | null>(null);
  const [loan, setLoan] = useState<Loan | null>(null);
  const invoiceRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchData = async () => {
      showLoading();
      try {
        let targetPaymentId = paymentId;
        if (loanId) {
          const { data: loanPayments } = await axios.get(`loans/${loanId}/payments`);
          const list = Array.isArray(loanPayments) ? loanPayments : Array.isArray(loanPayments?.data) ? loanPayments.data : [];
          targetPaymentId = list.reduce((max: number, p: any) => Math.max(max, Number(p?.id) || 0), 0) || undefined;
        }
        if (!targetPaymentId) {
          setPayment(null);
          setLoan(null);
          showAlert(t('visitOrders.noPayments'), "info");
          return;
        }
        const { data: paymentData } = await axios.get(`payments/${targetPaymentId}`);
        setPayment(paymentData);
        if (paymentData.loan.id) {
          const { data: loanData } = await axios.get(`loans/${paymentData.loan.id}`);
          setLoan(loanData);
        }
      } catch (error) {
        showAlert(t('common.loadError'), "error");
      } finally {
        hideLoading();
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [id, paymentId, loanId, isOpen, showAlert, showLoading, hideLoading, t]);

  // El abono puede traer la fecha en date, paymentDate o operationDate (antes se caía si no venía date).
  const paymentDate = String(payment?.date || (payment as any)?.paymentDate || (payment as any)?.operationDate || '');
  const formattedDate = paymentDate ? paymentDate.slice(0, 10).replaceAll("-", "") : t('visitOrders.dateUnavailable');
  const formattedTime = paymentDate ? paymentDate.slice(11, 19).replaceAll(":", "").replace(".", "") : t('visitOrders.timeUnavailable');

  const tcktNmbr = `${payment?.id}`;

  // Los botones quedan fuera del área que se convierte en PDF: no hace falta ocultarlos.
  const hidePdfButtons = () => undefined;
  const showPdfButtons = () => undefined;

  async function handlePrint() {
    if (invoiceRef.current && loan?.client?.fullName && payment) {
      const clientName = loan.client.fullName.replace(/\s+/g, "_");
      const fileName = t('visitOrders.receiptFileName', { client: clientName, number: tcktNmbr });

      hidePdfButtons();
      await new Promise((resolve) => setTimeout(resolve, 300));

      const pdfWidth = 75; // Ancho en mm (7.5 cm)
      const canvas = await html2canvas(invoiceRef.current!, {
        scale: 4,
        useCORS: true,
        scrollX: 0,
        scrollY: 0,
        width: invoiceRef.current!.scrollWidth,
        height: invoiceRef.current!.scrollHeight,
      });

      const imgData = canvas.toDataURL("image/png");
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: [pdfWidth, imgHeight],
      });

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, imgHeight, undefined, "FAST");
      pdf.save(fileName);

      showPdfButtons();
    }
  }

  async function handleSharePDF() {
    if (invoiceRef.current && clientName) {
      const formattedClientName = clientName.replace(/\s+/g, "_");
      const fileName = t('visitOrders.receiptFileName', { client: formattedClientName, number: tcktNmbr });

      hidePdfButtons();
      await new Promise((resolve) => setTimeout(resolve, 300));

      const pdfWidth = 75;
      const canvas = await html2canvas(invoiceRef.current!, {
        scale: 4,
        useCORS: true,
        scrollX: 0,
        scrollY: 0,
        width: invoiceRef.current!.scrollWidth,
        height: invoiceRef.current!.scrollHeight,
      });

      const imgData = canvas.toDataURL("image/png");
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: [pdfWidth, imgHeight],
      });

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, imgHeight, undefined, "FAST");
      const pdfBlob = pdf.output("blob");

      try {
        if (navigator.canShare && navigator.canShare({ files: [new File([pdfBlob], fileName, { type: "application/pdf" })] })) {
          const file = new File([pdfBlob], fileName, { type: "application/pdf" });
          await navigator.share({
            title: t('visitOrders.shareTitle'),
            text: t('visitOrders.shareText'),
            files: [file],
          });
        } else {
          const pdfUrl = URL.createObjectURL(pdfBlob);
          window.open(pdfUrl, "_blank");
          showAlert(t('visitOrders.shareUnsupported'), "info");
        }
      } catch (error) {
        showAlert(t('visitOrders.shareError'), "error");
      }

      showPdfButtons();
    }
  }

  const rows: [string, React.ReactNode][] = [
    [t('visitOrders.clientName'), loan?.client?.fullName ?? '—'],
    [t('visitOrders.clientIdNumber'), loan?.client?.idNumber ?? '—'],
    [t('visitOrders.clientAddress'), loan?.client?.mainAddress ?? '—'],
    [t('visitOrders.paymentDate'), paymentDate ? `${paymentDate.slice(0, 10)} ${paymentDate.slice(11, 16)}` : '—'],
  ];
  const summary: [string, React.ReactNode][] = [
    [t('visitOrders.totalValueLabel'), companyMoney(loan?.totalValue ?? 0)],
    // Forma de pago real del abono (efectivo o transferencia) y quién lo registró.
    [t('visitOrders.paymentMethodLabel'), payment ? (Boolean(Number((payment as any).cash ?? 0)) ? t('visitOrders.paymentMethodCashShort') : t('payments.transfer')) : '—'],
    [t('visitOrders.installmentNumberLabel'), payment?.id ?? '—'],
    [t('visitOrders.installmentValueLabel'), companyMoney(payment?.value ?? 0)],
    [
      t('visitOrders.receivedByLabel'),
      [payment?.user?.firstName, payment?.user?.lastName].filter(Boolean).join(' ').trim() || payment?.user?.username || '—',
    ],
  ];
  const list = (items: [string, React.ReactNode][]) => (
    <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', columnGap: 2, rowGap: 0.75 }}>
      {items.map(([label, value]) => (
        <React.Fragment key={label}>
          <Box component="dt" sx={{ typography: 'body2', color: 'grey.700' }}>{label}</Box>
          <Box component="dd" sx={{ m: 0, typography: 'body2', fontWeight: 600, textAlign: 'right', overflowWrap: 'anywhere' }}>{value}</Box>
        </React.Fragment>
      ))}
    </Box>
  );

  return (
    // Se muestra dentro del modal estándar (Recibo de pago). El comprobante es un documento para
    // descargar o compartir: se dibuja siempre como papel (fondo blanco y texto oscuro), también en modo oscuro.
    <Box sx={{ maxWidth: 375, mx: 'auto' }}>
      <Box
        ref={invoiceRef}
        sx={{ bgcolor: 'common.white', color: 'common.black', border: 1, borderColor: 'grey.300', borderRadius: 1, p: 2.5 }}
      >
        <Typography variant="h6" component="p" textAlign="center" sx={{ fontWeight: 700 }}>
          {t('visitOrders.receiptHeading')}
        </Typography>
        <Typography variant="body2" textAlign="center" sx={{ color: 'grey.700', mb: 2 }}>
          {t('visitOrders.receiptNumber', { number: tcktNmbr })}
        </Typography>
        {list(rows)}
        <Box sx={{ borderTop: 1, borderColor: 'grey.300', borderTopStyle: 'dashed', my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('visitOrders.paymentSummary')}</Typography>
        {list(summary)}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mt: 2, flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'flex-end' }}>
        <Button variant="outlined" startIcon={<ShareOutlinedIcon />} onClick={handleSharePDF} disabled={!payment}>
          {t('visitOrders.share')}
        </Button>
        <Button variant="contained" startIcon={<FileDownloadOutlinedIcon />} onClick={handlePrint} disabled={!payment}>
          {t('visitOrders.download')}
        </Button>
      </Box>
    </Box>
  );
};

export default ReceiptModal;
